import { cancel, confirm, isCancel } from "@clack/prompts";
import type { SpaceMeta } from "@wakeloop/protocol";
import type { WakeLoopProfile } from "../config/wakeloop-profiles-api";
import { buildWakeLoopRequestContextFromProfile } from "../config/wakeloop-request-context";
import type { createPresenter } from "../runtime/presenter/index";
import {
  buildPublicSpaceJoinConfirmPromptConfig,
  isPublicSpace,
} from "../ui/public-space-warning";
import { fetchSpaceMeta } from "./meta-api";
import { runSpacePasswordRetry } from "./password-flow";

export type SpaceJoinAccessBootstrapResult =
  | {
      status: "ready";
      password?: string;
      spaceMeta: SpaceMeta;
      publicSpace: boolean;
    }
  | {
      status: "cancelled";
    };

type JoinAccessBootstrapPresenter = Pick<
  ReturnType<typeof createPresenter>,
  "line"
>;

// Join access bootstrap owns password/meta/public-space truth only.
// Membership flow and connect handoff start after this returns ready.
export async function resolveSpaceJoinAccessBootstrap(input: {
  wakeLoopProfile: WakeLoopProfile;
  baseUrl: string;
  space: string;
  initialPassword?: string;
  resolvedView: "human" | "agent";
  presenter: JoinAccessBootstrapPresenter;
  canPromptJoinPassword: boolean;
  canConfirmPublicSpaceJoin: boolean;
}): Promise<SpaceJoinAccessBootstrapResult> {
  const accessState = await resolveJoinAccessState({
    wakeLoopProfile: input.wakeLoopProfile,
    baseUrl: input.baseUrl,
    space: input.space,
    initialPassword: input.initialPassword,
    presenter: input.presenter,
    canPromptJoinPassword: input.canPromptJoinPassword,
  });
  if (accessState.status === "cancelled") {
    return {
      status: "cancelled",
    };
  }

  const publicSpace = isPublicSpace(accessState.spaceMeta.passwordProtected);
  const confirmedPublicSpace = await confirmPublicSpaceJoinAccess({
    publicSpace,
    resolvedView: input.resolvedView,
    canConfirmPublicSpaceJoin: input.canConfirmPublicSpaceJoin,
  });
  if (!confirmedPublicSpace) {
    return {
      status: "cancelled",
    };
  }

  return {
    status: "ready",
    password: accessState.password,
    spaceMeta: accessState.spaceMeta,
    publicSpace,
  };
}

async function resolveJoinAccessState(input: {
  wakeLoopProfile: WakeLoopProfile;
  baseUrl: string;
  space: string;
  initialPassword?: string;
  presenter: JoinAccessBootstrapPresenter;
  canPromptJoinPassword: boolean;
}): Promise<
  | {
      status: "ready";
      password?: string;
      spaceMeta: SpaceMeta;
    }
  | {
      status: "cancelled";
    }
> {
  const requestContext = buildWakeLoopRequestContextFromProfile({
    wakeLoopProfile: input.wakeLoopProfile,
  });
  const accessResult = await runSpacePasswordRetry({
    initialPassword: input.initialPassword,
    interactive: input.canPromptJoinPassword,
    presenter: input.presenter,
    codePrefix: "space.join.password",
    execute: async (password) =>
      await fetchSpaceMeta({
        requestContext,
        baseUrl: input.baseUrl,
        space: input.space,
        password,
      }),
  });
  if (accessResult.status === "cancelled") {
    return {
      status: "cancelled",
    };
  }

  return {
    status: "ready",
    password: accessResult.password,
    spaceMeta: accessResult.result,
  };
}

async function confirmPublicSpaceJoinAccess(input: {
  publicSpace: boolean;
  resolvedView: "human" | "agent";
  canConfirmPublicSpaceJoin: boolean;
}): Promise<boolean> {
  if (!input.publicSpace) {
    return true;
  }
  if (input.resolvedView !== "human") {
    return true;
  }
  if (!input.canConfirmPublicSpaceJoin) {
    throw new Error(
      "joining a public space in non-interactive human mode requires explicit confirmation"
    );
  }

  const confirmed = await confirm({
    ...buildPublicSpaceJoinConfirmPromptConfig(),
    initialValue: true,
  });
  if (isCancel(confirmed)) {
    cancel("Cancelled.");
    return false;
  }
  return confirmed;
}
